/**
 * The shared game context -- the world, its input wiring, and the one
 * mutable `game` object that boot.js fills in and loop.js reads.
 *
 * All of this lived at module scope in main.js.  The state below was ~22
 * module-scope `let`s there, written by the async boot and read by the
 * frame loop through optional chains; gathering them into one explicit
 * object is what lets boot.js and loop.js live in their own files and
 * still mean the same variables.  The trap this file exists to avoid is
 * the one that made main.js a god-object: state shared by name across
 * async boundaries with no record of who owns it.  The comments on the
 * fields and the wiring are the originals, moved with their code.
 */
import * as THREE from "three";
import { loadExterior } from "../exterior.js";
import { buildLevel, ColliderGrid, Player, burrSnare } from "@catworld/engine";
import { DayCycle } from "../daycycle.js";
import { ChargeMeter } from "../hud.js";
import { Meow } from "../meow.js";
import { DoorInteraction } from "../door.js";
import { TouchControls, wantsTouch } from "../touch.js";
import { QuickChat } from "../chat.js";
import { BurrCue } from "../burrs.js";
import { PounceOracle, PounceArc } from "../pounce.js";
// The page: ui/index.js mounts every panel, toast and control and hands
// back the handles.  This file never looks an id up again.
import { ui } from "../ui/index.js";

export const game = {
  startPlaying() {
    if (!game.ready || !ui.join.classList.contains("hidden")) return;
    game.started = true;
    ui.overlay.classList.add("hidden");
    updateLookHint();
  },
  async loadExterior() {
    const report = await loadExterior(scene);
    level.triangles += report.exportTriangles;
    level.draws += report.chunks;
  },
  // Index 4 -- "5 . Tabby", the toon tabby.  The cat you are handed on load,
  // so it wants to be the best-looking one rather than the first authored.
  active: 4,

  // Jump is release-triggered now: player.js charges while input.jump is high
  // and launches when it falls.  Writing the key level straight into input.jump
  // broke under that rule -- a tap whose keydown and keyup both arrive between
  // two animation frames (a 16.7 ms window at 60 Hz, and EVERY tap once rAF is
  // throttled in a background tab) would never be seen high at all, and the
  // jump would be silently swallowed.  So the key maintains a held flag and a
  // pending-press latch, and the frame loop derives input.jump just before
  // player.update as "held, or pressed since the last frame", clearing the
  // latch only after player.update has consumed it.  A tap is then high for at
  // least one full frame and launches at power 1.0.
  jumpHeld: 0,
  jumpPressed: 0,
  // What the keyboard last drove input.jump to.  The loop writes input.jump
  // only on a keyboard edge or while the key is held; an idle keyboard leaves
  // the field alone, so a harness poking catGame.input.jump directly can still
  // drive a full charge-and-release end to end.
  jumpKbLast: 0,

  camYaw: 0, camPitch: 0.28, camDist: 2.0,
  started: false,
  dragging: false,

  customizer: null,

  // ---- the shared world ------------------------------------------------------
  // All five are null until the async boot below finishes, and every use site is
  // optional-chained: the game has to be playable from the first frame, and it
  // is drawn for several seconds before the cats have even loaded.
  net: null,
  /**
   * The thumb controls, or null where they were never built.
   *
   * Constructed at module load rather than with the rest of the world below,
   * because the first touch has to both reveal the controls AND start the game,
   * and that touch can land while the cats are still loading.  Everything it
   * reaches for -- customizer, join, purr -- is optional-chained for the same
   * reason.
   */
  touch: null,
  happyHud: null,
  purr: null,
  butterflies: null,
  /** The mice and birds -- server-driven, wired in one block in the boot
      below; null until then, optional-chained everywhere like the rest. */
  prey: null,
  /** The dog -- same bargain as the prey: server-driven, one wiring block. */
  dog: null,
  join: null,
  /** Our own cat's happiness, off the last snapshot.  Mirrored here rather than
      read through net each frame because the purr and the meter both want it and
      neither should know what a slot is. */
  myHappy: 0,
  /** Slot -> name, kept from the roster, so a bliss toast can say whose cat it
      was without the wire carrying a name in every one. */
  catNames: new Map(),
  /**
   * Waiting to be put where our cat actually is.
   *
   * A cat that has been wandering for an hour is not at the spawn, and dropping
   * the player on the front lawn every time would undo the whole point of the
   * cat staying on the server.  So the local player is teleported to the
   * server's position for our slot the first time a snapshot carries one --
   * which is a frame or two after the claim, not at it, because the claim
   * arrives on the socket and positions arrive in the snapshot stream.
   */
  seatWanted: false,
  /** Whether a panel owned the screen last frame; see the frame loop. */
  wasPanelled: false,

  camFocused: false,
  ready: false,
  /** @type {DoorInteraction | null} */
  door: null,

  // The developer overlay.  loop.js constructs it beside the frame loop it
  // watches and hangs it here, where the digit-key gate below can read it.
  debug: null,
};

const canvas = ui.canvas;
/**
 * Whether this is a device that will be played with thumbs.
 *
 * Asked once, here, because it decides two things that cannot be changed
 * cheaply afterwards: the renderer's pixel ratio and the shadow map's size.
 * Everything else about the touch layer is decided later, on the first real
 * touch -- see touch.js's wantsTouch.
 */
const COARSE = wantsTouch();

const renderer = new THREE.WebGLRenderer({ canvas, antialias: !COARSE });
// A modern phone reports devicePixelRatio 3, and honouring even half of that
// is four times the fragments of a DPR-1 desktop -- for a scene that already
// carries sixteen skinned cats, a 2048 shadow map and a full street.  1.5 is
// where a 390 pt phone still renders text and fence rails crisply while the
// fill cost stays inside what a mid-range GPU does at 60 fps.  Antialiasing
// goes with it: at 1.5x the supersample is already doing that job.
renderer.setPixelRatio(Math.min(devicePixelRatio, COARSE ? 1.5 : 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
// 1.08, down from 1.14.  The environment map below is matched to the fill it
// replaced for DIFFUSE light, but it also gives every material in the level a
// specular reflection it did not have, and a few percent of extra highlight on
// every surface at once reads as the whole street lifting.  Measured back:
// against the same six camera poses in the previous build, mean frame luminance
// now lands within 1.3%.
renderer.toneMappingExposure = 1.08;

const scene = new THREE.Scene();

/**
 * The sky, the sun, the moon, the fog and every ambient term -- the whole
 * day/night cycle -- live in daycycle.js now.  Everything that used to be
 * pinned here (the SKY/ENVIRONMENT gradient pair and the reason they
 * differ, the PMREM width trap, the solved 0.32/0.85 ambient split, the
 * shadow-map sizing) moved there WITH its comments, because the cycle has
 * to recolour all of it in one coordinated motion twenty-four "hours" a
 * day.  What time it is belongs to the server (the `day` message; the
 * handler is wired in the boot below beside the other world state), and
 * daycycle.js's header says how the clock is anchored and eased.  The
 * frame loop calls daycycle.update(dt); updateCamera calls
 * daycycle.follow(player.pos) where it used to place the fixed sun.
 */
const daycycle = new DayCycle({ renderer, scene, coarse: COARSE });

const camera = new THREE.PerspectiveCamera(58, 1, 0.05, 200);

const level = buildLevel(scene);
console.log("level:", level.triangles, "tris,", level.draws, "draw calls,",
            level.colliders.length, "colliders");
console.table?.(level.report);

const grid = new ColliderGrid(level.colliders, 4);
// surfaceY, not groundY: the cat stands on the paving and the sod, not on the
// earth they are laid on.  level/index.js says why that is not the same thing.
const player = new Player(grid, level.surfaceY);
player.pos.set(level.spawn.x, level.spawn.y, level.spawn.z);
const cats = [];

// The aim.  Same grid and same ground the player itself resolves against --
// the arc is flown by a second Player on exactly this world, so anything the
// real cat can be stopped by, the preview is stopped by too.  Added straight
// to the scene beside the level (not through the Builder, which merges
// geometry and hands out colliders -- this is neither).
const pounceOracle = new PounceOracle(grid, level.surfaceY);
const pounceArc = new PounceArc();
scene.add(pounceArc.object3D);

// ---- input -----------------------------------------------------------------
const input = { forward: 0, back: 0, left: 0, right: 0, sprint: 0, jump: 0, sitToggle: 0, stalk: 0, swipe: 0, claw: 0 };
game.door = new DoorInteraction({
  player, scene, getNet: () => game.net,
  onTravel: (landing) => {
    player.pos.set(landing.x, landing.y, landing.z);
    player.vel.set(0, 0, 0); player.facing = landing.facing;
    player.clinging = false; player.clingBox = null; player.grounded = true;
    player.charging = false; player.chargeTime = 0;
    input.jump = 0; game.jumpHeld = 0; game.jumpPressed = 0; game.jumpKbLast = 0;
    game.camYaw = (landing.facing + Math.PI) % (Math.PI * 2);
    game.camPitch = .2;
    snapCamera();
    // Put the camera on the arrival side immediately; easing from its old
    // outdoor position would briefly show the back of the closed door.
    updateCamera(0);
    camera.position.copy(camWant);
    camera.lookAt(camTarget);
  },
});
// Held/pressed as typed; the frame loop gates these on whether the cat being
// played actually has the clip, so the generated cats never enter a state they
// cannot show.
const raw = { stalk: 0, swipe: 0 };
const KEYS = {
  KeyW: "forward", ArrowUp: "forward", KeyS: "back", ArrowDown: "back",
  KeyA: "left", ArrowLeft: "left", KeyD: "right", ArrowRight: "right",
  ShiftLeft: "sprint", ShiftRight: "sprint", Space: "jump",
};
addEventListener("keydown", (e) => {
  if (!game.started && !game.join?.isOpen && (e.code in KEYS || e.code.startsWith("Digit"))) {
    game.started = true;
    overlay.classList.add("hidden");
    updateLookHint();
  }
  if (e.code in KEYS) {
    // Space keeps its KEYS entry for the start-gate above and for the
    // preventDefault (the page must not scroll), but its level is never
    // written here -- the frame loop derives it from the pair above.
    if (e.code === "Space") { game.jumpHeld = 1; game.jumpPressed = 1; }
    else input[KEYS[e.code]] = 1;
    e.preventDefault();
  }
  if (e.code === "ControlLeft" || e.code === "ControlRight") { raw.stalk = 1; e.preventDefault(); }
  if (e.code === "KeyC") input.sitToggle = 1;
  // V is the voice.  !e.repeat because a held key auto-repeats at keyboard
  // rate, and meow.js's own rate limit would turn that into a metronome.
  if (e.code === "KeyV" && !e.repeat) meowNow();
  if (e.code === "KeyF") raw.swipe = 1;
  if (e.code === "KeyR") respawn();
  if (e.code === "KeyM") game.purr?.toggleMute();
  // B, not C -- C has been sit since before there was a builder to bind.
  if (e.code === "KeyB") game.customizer?.toggle();
  // Changing cat is a developer affordance now, not part of playing, so the
  // number keys are live only while the overlay that documents them is up --
  // otherwise a stray 3 during a sprint silently swaps the cat out from under
  // the player, with nothing on screen to explain what happened or undo it.
  if (game.debug.visible && e.code.startsWith("Digit")) {
    const n = Number(e.code.slice(5));
    if (n >= 1 && n <= 5) switchCat(n - 1);
  }
});
addEventListener("keyup", (e) => {
  if (e.code in KEYS) {
    if (e.code === "Space") game.jumpHeld = 0;
    else input[KEYS[e.code]] = 0;
    e.preventDefault();
  }
  if (e.code === "ControlLeft" || e.code === "ControlRight") raw.stalk = 0;
});

const overlay = ui.overlay;
const lookHint = ui.lookHint;
// ui/charge.js makes the element now, so it always exists: the old "a stale
// page loses the meter, not the game" posture died with the markup's move.
const meter = new ChargeMeter(ui.charge);

const locked = () => document.pointerLockElement === canvas;

/**
 * Start playing.
 *
 * Pointer lock is requested but never depended on: inside an iframe without
 * allow="pointer-lock", and on some file:// setups, the request is simply
 * refused.  Gating the title card on the lock succeeding leaves the game
 * looking dead, so the click always starts it and look control falls back to
 * click-and-drag.
 */
function begin() {
  // Naming the cat comes first.  The card stops its own bubbling events, but
  // the canvas is a metre of unobstructed target behind it and window keydown
  // fires wherever focus is; without this gate the title card lifts and the
  // player is walking around under a dialog they have not answered.
  if (game.join?.isOpen) return;
  if (!game.started) {
    game.started = true;
    overlay.classList.add("hidden");
  }
  // An AudioContext built before a user gesture is born suspended, so the
  // purr can only ever wake up here.  Idempotent, and silent where WebAudio
  // is missing entirely.
  game.purr?.resume();
  meow.resume();      // same policy, its own context; see meow.js
  // Not on a touch device: there is no pointer to lock, and asking for it
  // costs a rejected promise and, on some Android browsers, a full-screen
  // permission prompt over the game on the very first tap.
  const req = game.touch?.active ? null : canvas.requestPointerLock?.({ unadjustedMovement: false });
  if (req && typeof req.catch === "function") req.catch(() => updateLookHint());
  setTimeout(updateLookHint, 60);
}

function updateLookHint() {
  // Unlocked, the hint teaches the button that does NOT claw: dragging with
  // the left one works too, but it hangs the claws out while you do it.
  // The touch layer hides this line entirely (see index.html), so the third
  // case is only ever read in the moment between the first touch and the
  // class landing on the root.
  lookHint.textContent = game.touch?.active ? "drag the right of the screen to look"
    : locked() ? "mouse look · esc to release"
    : "right-drag to look around";
  lookHint.classList.toggle("hidden", !game.started);
}

// Left mouse is claws as well as drag-to-look.  The two coexist: both are
// held states, and clawing at nothing does nothing.  Two carve-outs, though:
// the click that starts the game from the title card is captured *before*
// begin() flips `started`, so the "click to play" click never bares the
// claws -- and e.button is checked, so a right or middle click can drag but
// never claw.
/**
 * A mouse event that is really the ghost of a touch.
 *
 * touch.js preventDefaults every touch, which is supposed to suppress the
 * compatibility mouse events -- but the browsers disagree about when, and a
 * ghost mousedown on the canvas is read three lines below as the claws coming
 * out.  A cat walked with the left thumb would flex its claws at nothing.
 * 700 ms covers the ~300 ms the compatibility path classically waits plus
 * room; a window rather than a permanent flag so a touchscreen laptop keeps
 * its mouse.
 */
const ghost = () => game.touch && performance.now() - game.touch.lastTouch < 700;

canvas.addEventListener("mousedown", (e) => {
  if (ghost()) return;
  game.dragging = true;
  const wasStarted = game.started;
  begin();
  if (e.button === 0 && wasStarted) input.claw = 1;
});
// Right-drag has to look without clawing, and it cannot do that while the
// context menu is opening on top of it.  This matters exactly where pointer
// lock is refused -- inside an iframe, on file:// -- because there look IS a
// drag, and with the claws on the left button the only way to look around
// would otherwise be to hold them out the whole time.  Left-drag still looks
// and still claws; the two only had to stop being the same gesture.
canvas.addEventListener("contextmenu", (e) => { if (game.started) e.preventDefault(); });
addEventListener("mouseup", (e) => {
  if (ghost()) return;
  game.dragging = false;
  if (e.button === 0) input.claw = 0;
});
document.addEventListener("pointerlockchange", updateLookHint);
document.addEventListener("pointerlockerror", updateLookHint);

addEventListener("mousemove", (e) => {
  if (ghost() || (!locked() && !game.dragging)) return;
  game.camYaw -= e.movementX * 0.0026;
  game.camPitch = THREE.MathUtils.clamp(game.camPitch + e.movementY * 0.0021, -0.30, 1.02);
});
/**
 * Look sensitivity for a thumb, radians per CSS pixel.
 *
 * The mouse just above uses 0.0026 / 0.0021, tuned for a hand with a desk to
 * cross.  A thumb gets perhaps 200 px before it has to lift and re-plant, and
 * one sweep should be worth most of a half turn -- so about 2.5x.  Pitch
 * keeps the mouse's sign so drag-down looks down on both.
 */
const LOOK_TOUCH_X = 0.0065;
const LOOK_TOUCH_Y = 0.0052;

addEventListener("wheel", (e) => {
  game.camDist = THREE.MathUtils.clamp(game.camDist + e.deltaY * 0.0016, 0.9, 5.0);
}, { passive: true });

function respawn() {
  player.pos.set(level.spawn.x, level.spawn.y + 0.4, level.spawn.z);
  player.vel.set(0, 0, 0);
  // The claws do not survive the trip: a cling left set would hold the cat
  // frozen in mid-air at the spawn, hanging from a fence a street away.
  player.clinging = false;
  player.clingBox = null;
  snapCamera();          // a teleport is not a climb; do not ease across it
}

// ---- meow ------------------------------------------------------------------
// The cat's voice on demand: the V key, the #meow-btn pill, and the touch pad
// all land here.  Constructed at module load for the same reason the touch
// layer is -- a press can arrive while the cats are still loading -- and
// every route in is a real user gesture, so play() can resume the suspended
// AudioContext itself; begin() resumes it too, purr-style, for good measure.
// The mute hook defers to the live purr (one toggle governs the cat's whole
// voice); before the purr is built, meow.js falls back to the stored key.
// play() returns the utterance's length, or 0 when the press was swallowed
// by the rate limit, and the chin-lift cue (cat.js) matches the sound's
// duration -- or plays alone for a muted player, who opted out of the sound,
// not the cat.  Local to this client on purpose: broadcasting the meow will
// ride the chat message once that lands.
const meow = new Meow({ muted: () => game.purr?.muted });
function meowNow() {
  if (game.customizer?.isOpen || game.join?.isOpen) return;   // panels own the keys
  const dur = meow.play();
  if (dur) cats[game.active]?.meow(dur);
  game.door?.meow();
}
ui.meowBtn.addEventListener("click", (e) => {
  e.stopPropagation();          // a click on the pill is not "start playing"
  meowNow();
});

game.touch = new TouchControls({
  canvas,
  root: ui.touch,
  input,
  hooks: {
    begin: () => begin(),
    // The same two lines the mouse look runs, and deliberately not a shared
    // helper: the sensitivities differ by 2.5x and the clamp is the one
    // thing they must agree on, so it reads better duplicated than
    // parameterised.
    look: (dx, dy) => {
      game.camYaw -= dx * LOOK_TOUCH_X;
      game.camPitch = THREE.MathUtils.clamp(game.camPitch + dy * LOOK_TOUCH_Y, -0.30, 1.02);
    },
    zoom: (d) => { game.camDist = THREE.MathUtils.clamp(game.camDist + d, 0.9, 5.0); },
    // Straight into the release-triggered latch the keyboard uses, so a
    // thumb and the Space bar gather exactly the same pounce.
    jumpDown: () => { game.jumpHeld = 1; game.jumpPressed = 1; },
    jumpUp: () => { game.jumpHeld = 0; },
    meow: () => meowNow(),      // one-shot; see the meow block above
    blocked: () => !!(game.customizer?.isOpen || game.join?.isOpen),
  },
});
// The title card teaches the controls, so on a device that has neither a
// click nor a W it has to say something else.  Only the coarse-pointer path
// ever reads this: a device revealed by its first touch has already had that
// touch start the game and hide the card.
if (game.touch.active) {
  const hint = ui.overlayHint;
  if (hint) hint.textContent = "touch anywhere to play";
}

// ---- quick-chat ------------------------------------------------------------
// The import, this block, and one line in the frame loop (after updateCamera,
// so the bubbles project against the camera pose this frame actually renders)
// -- everything else lives in chat.js.  Constructed at module load like the
// touch layer and for the same reason: T (or the say pad) has to answer the
// moment somebody presses it, and everything it needs that does not exist yet
// -- net above all -- is fetched through these closures at call time, never
// captured.
const chat = new QuickChat({
  menu: ui.chat,
  layer: ui.bubbles,
  camera,
  grid,
  getNet: () => game.net,
  blocked: () => !!(game.customizer?.isOpen || game.join?.isOpen),
  // Our own bubble (slot -1 at pick time, or our claimed slot if a message
  // ever names it) anchors to the locally simulated player, which leads the
  // server's snapshot of us by a round trip; everyone else anchors to the
  // roster rig the interpolator is already posing, so a bubble tracks its
  // cat exactly as DRAWN, never as wired.
  anchorFor: (slot, out) => {
    if (slot < 0 || (game.net && slot === game.net.slot)) {
      out.x = player.pos.x; out.y = player.pos.y; out.z = player.pos.z;
      return true;
    }
    const e = game.net?.roster.slots[slot];
    if (!e || !e.placed) return false;
    const p = e.cat.root.position;
    out.x = p.x; out.y = p.y; out.z = p.z;
    return true;
  },
});
game.touch.hooks.say = () => chat.toggle();

// ---- burrs -----------------------------------------------------------------
// The import, this block, and nothing else -- the mechanic lives in
// packages/engine/src/burrs.js and packages/engine/src/player.js.  The snare is read off the SAME
// level build the renderer just drew (the "burr:" markers), so the bushes
// the player can see and the bushes that snag are one list by construction;
// the server judges the happiness sting from its own copy of that build,
// and this side only ever makes the cat move less.  Works offline too --
// artifact.html has bushes and tangles, just no sting -- which is why it
// wires here rather than in the networked boot below; the cue fetches the
// (still-null) net through a closure at call time, chat.js's pattern.
player.burrs = burrSnare(level.markers);
new BurrCue(ui.burr, { player, getNet: () => game.net });

/** Put the player where the server says our cat is standing. */
export function takeSeat() {
  if (!game.net || game.net.slot < 0) return;
  const s = game.net.client.sampleLive(game.net.slot);
  if (!s) return;                       // no snapshot bracketing yet; try again
  game.seatWanted = false;
  player.pos.set(s.x, s.y, s.z);
  player.vel.set(0, 0, 0);
  player.facing = s.facing;
  player.clinging = false;
  player.clingBox = null;
  snapCamera();                         // arriving is not a walk; do not ease
}

/**
 * The name over the HUD.  A built cat that has been named wears its own name;
 * everything else falls back to the base cat's, so the line is never blank.
 */
export function showCatName() {
  const base = cats[game.active] ? cats[game.active].meta.display_name : "";
  ui.catName.textContent =
    game.customizer ? game.customizer.displayName(base) : base;
}

export function switchCat(i) {
  if (i === game.active || !cats[i]) return;
  cats[game.active].root.visible = false;
  game.active = i;
  cats[game.active].root.visible = true;
  // charge, not just the state: mid-pounce the pose is seeked from it, and a
  // switch that omitted it would hand the incoming cat a charge of zero and
  // show one frame of shallow crouch before the loop supplied the real value.
  cats[game.active].play(player.state, { fade: 0, charge: player.charge });
  // Before the name: the look carries across a base change, and setActive is
  // what puts it on the incoming cat.
  game.customizer?.setActive(i);
  showCatName();
  for (const el of ui.catButtons) {
    el.classList.toggle("on", Number(el.dataset.cat) === i);
  }
}
for (const el of ui.catButtons) {
  el.addEventListener("click", (e) => {
    e.stopPropagation();
    switchCat(Number(el.dataset.cat));
  });
}

// ---- camera ----------------------------------------------------------------
const camTarget = new THREE.Vector3();
const camWant = new THREE.Vector3();
const camFocus = new THREE.Vector3();     // the point the camera orbits and looks at
const ray = new THREE.Ray();
const hit = new THREE.Vector3();

/**
 * How fast the camera's focus follows the cat vertically.
 *
 * The cat mounts a stair in ONE frame: player.js tryStep lifts it by the whole
 * riser, moves it, and settles it, so pos.y goes up 0.183 m between two
 * frames on every flight in the level.  That is right for the cat -- a cat
 * does step onto things in one movement -- and it was being handed straight
 * to lookAt, which turned each tread into a 5 degree pitch snap.  Measured on
 * the porch: three treads, three 5 degree lurches, which is the chop.
 *
 * So the focus is damped in Y only.  X and Z still track exactly, because
 * lateral lag makes a follow camera feel drunk, and nothing in the level
 * moves the cat sideways in a single frame the way a step moves it upward.
 *
 * Two constants because a stair and a jump want opposite things.  Grounded,
 * 0.085 s spreads a riser over about five frames -- gone as a snap, still far
 * too quick to read as the camera lagging.  Airborne, the cat is under
 * gravity and its motion is already continuous, so there is no snap to hide
 * and damping only makes a jump feel floaty; 0.03 s is near-rigid and keeps
 * the arc crisp.  On a slope, where dy is continuous either way, 0.085 s
 * costs 18 mm of lag at a sprint up the 9% ramp.
 */
const CAM_TAU_GROUND = 0.085;
const CAM_TAU_AIR = 0.030;
// Past this the cat did not climb, it moved: respawn, or a debug teleport.
// Easing 1.2 m of that would be a swoop across the level.
const CAM_SNAP = 1.0;

/**
 * How far the camera must stay above whatever is underneath it.
 *
 * grid.raycast only knows colliders, and the terrain deliberately has none --
 * the ground is a heightfield the player floors against as a FUNCTION, so
 * there is no box under the lawn for a ray to hit.  Nothing was therefore
 * stopping the orbit going through the world: the pitch floor of -0.30 rad
 * puts the camera at 0.30 + dist*sin(pitch) relative to the cat, which is
 * below ground for any orbit past about a metre, and at the full 5 m orbit is
 * 1.18 m under it.
 *
 * Clamping the height rather than tightening the pitch on purpose: the pitch
 * limit that would be safe at 5 m is needlessly restrictive at 1 m, and this
 * way the camera simply slides along the ground when you push into it, which
 * is what a follow camera is expected to do.  Indoors it is inert -- the
 * ground under a floor is the earth a storey below -- and the collider
 * raycast already handles interior floors, which do have boxes.
 */
const CAM_CLEAR = 0.18;
function liftAboveGround(v) {
  const floor = level.surfaceY(v.x, v.z) + CAM_CLEAR;
  if (v.y < floor) v.y = floor;
  return v;
}

/** Put the focus on the cat immediately -- for spawns and teleports. */
function snapCamera() {
  camFocus.set(player.pos.x, player.pos.y + 0.30, player.pos.z);
  game.camFocused = true;
}

export function updateCamera(dt) {
  const wantY = player.pos.y + 0.30;
  if (!game.camFocused) snapCamera();
  camFocus.x = player.pos.x;
  camFocus.z = player.pos.z;
  if (Math.abs(wantY - camFocus.y) > CAM_SNAP) {
    camFocus.y = wantY;
  } else {
    const tau = player.grounded ? CAM_TAU_GROUND : CAM_TAU_AIR;
    camFocus.y += (wantY - camFocus.y) * (1 - Math.exp(-dt / tau));
  }
  camTarget.copy(camFocus);
  const cp = Math.cos(game.camPitch);
  const dir = new THREE.Vector3(
    Math.sin(game.camYaw) * cp, Math.sin(game.camPitch), Math.cos(game.camYaw) * cp);

  // Pull in if the world is between the cat and the camera, so ducking behind
  // a crate never leaves the player staring at the inside of it.
  ray.set(camTarget, dir);
  const dist = Math.max(0.45, grid.raycast(ray, game.camDist) - 0.14);

  camWant.copy(camTarget).addScaledVector(dir, dist);
  liftAboveGround(camWant);
  camera.position.lerp(camWant, 1 - Math.pow(0.0016, dt));
  // Again after the lerp: the straight line between two points that are both
  // above ground still dips below it across a creek bank or the ramp's crest.
  liftAboveGround(camera.position);
  camera.lookAt(camTarget);

  // The key light rides the player exactly as the fixed sun did; where in
  // the sky it sits is daycycle's answer, not the camera's.
  daycycle.follow(player.pos);
}

// ---- the context, assembled ------------------------------------------------
// The fixed world objects join the mutable fields above, so boot.js, loop.js
// and the window.catGame seam all hold one object.  Same references, never
// copies: game.cats IS the array the boot pushes the loaded cats into.
Object.assign(game, { renderer, scene, camera, daycycle, level, grid, player,
                      cats, input, raw, meter, chat,
                      // The aim: built with the level above, same grid and
                      // ground as the player, so the preview is stopped by
                      // whatever stops the cat.
                      pounceOracle, pounceArc });
